/**
 * IONLOOP Assistant — slash-command router.
 *
 * Recognises input starting with /, routes it to a registry of deterministic
 * JavaScript functions. Commands are NOT sent through the knowledge retrieval
 * pipeline and they NEVER reach a shell or eval.
 *
 * Single source of truth for the command list, used by autocomplete, /help,
 * and the executor.
 */

var categoriesMod = require('./knowledge/categories');
var CATEGORIES = categoriesMod.CATEGORIES;

// ---------------------------------------------------------------------------
// Levenshtein (inline, small, no external dependency)
// ---------------------------------------------------------------------------
function levenshtein(a, b) {
    var m = a.length, n = b.length;
    if (m === 0) { return n; }
    if (n === 0) { return m; }
    var prev = [], curr = [];
    var i, j;
    for (j = 0; j <= n; j += 1) { prev[j] = j; }
    for (i = 1; i <= m; i += 1) {
        curr[0] = i;
        for (j = 1; j <= n; j += 1) {
            curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        }
        for (j = 0; j <= n; j += 1) { prev[j] = curr[j]; }
    }
    return prev[n];
}

// ---------------------------------------------------------------------------
// Command registry
// ---------------------------------------------------------------------------
var COMMANDS = {};

function register(name, aliases, description, execute) {
    COMMANDS[name] = {
        name: name,
        aliases: aliases || [],
        description: description || '',
        execute: execute
    };
}

register('clear', ['cls'], 'Clear the current conversation.', function (ctx) {
    ctx.conversation.clear();
    return { action: 'clear', message: { status: 'answered', type: 'system', intro: 'Conversation cleared.' } };
});

register('reset', [], 'Reset Assistant session context.', function (ctx) {
    ctx.conversation.clear();
    if (ctx.resetCategory) { ctx.resetCategory(); }
    return { action: 'reset', message: { status: 'answered', type: 'system', intro: 'Assistant reset. Starting fresh.' } };
});

register('help', [], 'Show available commands.', function () {
    var rows = getCanonicalCommands().map(function (c) {
        return '/' + c.name + '  \u2014  ' + c.description;
    });
    return {
        action: null,
        message: { status: 'answered', type: 'command-help', title: 'IONLOOP Assistant commands', intro: 'Commands are handled locally, before retrieval.', bullets: rows }
    };
});

register('topics', ['categories', 'areas'], 'Show available IONLOOP knowledge areas.', function (ctx) {
    return ctx.getCategoryCounts().then(function (counts) {
        var categories = CATEGORIES.map(function (cat) {
            return { key: cat.key, label: cat.label, count: (counts && counts[cat.key]) || 0 };
        });
        return {
            action: null,
            message: { status: 'answered', type: 'command-topics', title: 'IONLOOP knowledge areas', intro: 'Select an area to explore it.', categories: categories }
        };
    });
});

register('sources', [], 'Show sources used for the most recent answer.', function (ctx) {
    var lastAnswer = ctx.getLastAnswer ? ctx.getLastAnswer() : null;
    var sources = (lastAnswer && lastAnswer.sources) || [];
    if (!sources.length) {
        return { action: null, message: { status: 'answered', type: 'system', intro: 'No answer sources are available yet.' } };
    }
    return { action: null, message: { status: 'answered', type: 'command-sources', title: 'Sources for the previous answer', sources: sources } };
});

register('about', [], 'About IONLOOP Assistant.', function () {
    return {
        action: null,
        message: { status: 'answered', type: 'system', title: 'IONLOOP Assistant', intro: 'A native knowledge assistant that answers questions using IONLOOP\u2019s own indexed product and documentation content.', paragraphs: ['No external AI provider is required for these answers.'] }
    };
});

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------
function resolveCommandName(name) {
    if (!name) { return null; }
    if (COMMANDS[name]) { return name; }
    var keys = Object.keys(COMMANDS);
    for (var i = 0; i < keys.length; i += 1) {
        if (COMMANDS[keys[i]].aliases.indexOf(name) !== -1) {
            return keys[i];
        }
    }
    return null;
}

function suggestCommand(name) {
    var best = null;
    var bestDist = 3;
    var keys = Object.keys(COMMANDS);
    for (var i = 0; i < keys.length; i += 1) {
        var d = levenshtein(name, keys[i]);
        if (d < bestDist) {
            bestDist = d;
            best = keys[i];
        }
        (COMMANDS[keys[i]].aliases || []).forEach(function (alias) {
            var da = levenshtein(name, alias);
            if (da < bestDist) {
                bestDist = da;
                best = keys[i];
            }
        });
    }
    return best;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------
function parseCommand(input) {
    if (typeof input !== 'string') { return null; }
    var trimmed = input.trim();
    if (trimmed.charAt(0) !== '/') { return null; }
    var rest = trimmed.slice(1).trim();
    var parts = rest.split(/\s+/);
    var rawName = (parts[0] || '').toLowerCase();
    var args = parts.slice(1);
    var rawArgs = rest.replace(/^\S+\s*/, '');
    var canonical = resolveCommandName(rawName);
    if (!canonical) {
        return { command: rawName, args: args, rawArgs: rawArgs, valid: false, canonical: null, rawInput: input };
    }
    return { command: canonical, args: args, rawArgs: rawArgs, valid: true, canonical: canonical, rawInput: input };
}

// ---------------------------------------------------------------------------
// Command list for autocomplete
// ---------------------------------------------------------------------------
function getCanonicalCommands() {
    return Object.keys(COMMANDS).map(function (key) {
        return { name: key, description: COMMANDS[key].description };
    });
}

function getCommands(filter) {
    var list = getCanonicalCommands();
    var q = (typeof filter === 'string' ? filter.toLowerCase().trim() : '');
    if (!q) { return list; }
    return list.filter(function (c) { return c.name.indexOf(q) === 0; });
}

// ---------------------------------------------------------------------------
// Executor
// ---------------------------------------------------------------------------
function executeCommand(input, ctx) {
    var parsed = parseCommand(input);

    if (!parsed || !parsed.valid || !COMMANDS[parsed.canonical]) {
        // Unknown command — produce a safe message without calling search.
        var unknown = parsed ? parsed.command : '';
        var suggestion = unknown ? suggestCommand(unknown) : null;
        var paragraphs = suggestion
            ? ['Did you mean /' + suggestion + '?', 'Type /help to see available commands.']
            : ['Type /help to see available commands.'];
        return Promise.resolve({
            action: null,
            message: { status: 'answered', type: 'system', intro: 'Unknown command: /' + unknown + '.', paragraphs: paragraphs }
        });
    }

    try {
        var result = COMMANDS[parsed.canonical].execute(ctx, parsed.args, parsed);
        return Promise.resolve(result);
    } catch (err) {
        return Promise.resolve({
            action: null,
            message: { status: 'answered', type: 'system', intro: 'Command failed: ' + err.message }
        });
    }
}

module.exports = {
    COMMANDS: COMMANDS,
    parseCommand: parseCommand,
    executeCommand: executeCommand,
    getCommands: getCommands,
    getCanonicalCommands: getCanonicalCommands,
    suggestCommand: suggestCommand,
    resolveCommandName: resolveCommandName
};